"use client"
import { useCallback, useEffect, useState } from "react"

import { isSameDay, parseISO } from "date-fns"
import { useRouter, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"

import { PMS_SYSTEM_WHIMSTAY } from "@/constant"
import { PropertyTypeV2 } from "commonModule/services/common"
import { openDetailPageCalendar } from "components/date-picker/date-range-input/CalendarPopOver"
import { useIsAgent } from "hooks/useIsAgent"
import {
  markAuthStore,
  selectIsTrmsncndAcceptedByUser,
  selectIsUserLoggedIn,
  selectUser,
} from "stores/auth/authSlice"
import {
  loadPrequote,
  markBookingStore,
  selectBookingStore,
} from "stores/booking/bookingSlice"
import { useAppDispatch, useAppSelector } from "stores/hooks"
import { getCookie, setCookie } from "utils/cookies"
import { generateCustomUUID } from "utils/helper"
import { getRefTaPropertyId } from "utils/local-storage"

export const useBookingSectionController = ({
  property,
}: {
  property: PropertyTypeV2
}) => {
  const t = useTranslations("detailPage")
  const router = useRouter()
  const searchParams = useSearchParams()
  const dispatch = useAppDispatch()

  const isLoggedIns = useAppSelector(selectIsUserLoggedIn)
  const user = useAppSelector(selectUser)
  const isTrmsncndAcceptedByUser = useAppSelector(
    selectIsTrmsncndAcceptedByUser,
  )
  const bookingStore = useAppSelector(selectBookingStore)

  const isAgent = useIsAgent()
  // Treat as a travel agent client only on the property this ref_ta referral is
  // bound to. Reading the binding (not the URL) blocks the auto-appended ref_ta
  // leak on other properties, and keeps pricing on the referred property even
  // when the client returns to it from search without ref_ta in the URL.
  const boundPropertyId = getRefTaPropertyId()
  const isTravelAgentClient =
    !!boundPropertyId && boundPropertyId === property.property_id

  const [isLoggedIn, setIsLoggedIn] = useState(isLoggedIns)
  const [loaderDots, setLoaderDots] = useState(false)

  useEffect(() => {
    setIsLoggedIn(isLoggedIns)
  }, [isLoggedIns])

  useEffect(() => {
    dispatch(markBookingStore({ isPaymentPage: false }))
  }, [dispatch])

  useEffect(() => {
    dispatch(
      markBookingStore({
        isTravelProtected: false,
        totalAmt: (bookingStore?.totalAmt || 0) + (bookingStore?.discount || 0),
        discount: 0,
        promocode: null,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    function loadData() {
      const search = window.location.search
      const params = new URLSearchParams(search)

      const dataToken = params.get("dataToken") || ""
      const isgoogle =
        params?.get("isgoogle") === "true" || getCookie("isGoogle") === "true"
      const searchPriceParam = params.get("searchPrice") || ""
      const timestampParam = params.get("timestamp")
      const quoteId = params.get("quoteId") || ""

      // Only agents and clients can load pricing
      if (!isAgent && !isTravelAgentClient) return
      if (property.status !== "LIVE" && dataToken !== "E76xOs7B2s0") {
        return
      }

      const isGvr =
        params?.get("isgoogle") === "true" &&
        params?.get("utm_source") === "gvr"

      setCookie("directIncentive", isGvr ? "true" : "false", 1)

      if (
        (property.status === "LIVE" || dataToken) &&
        bookingStore.startDate &&
        bookingStore.endDate &&
        bookingStore.dayCount > 0 &&
        (property.pms_system === PMS_SYSTEM_WHIMSTAY ||
          bookingStore.adultNumber + bookingStore.childNumber <=
            property.max_no_guests) &&
        !(!property?.suitable_pet && bookingStore.pet)
      ) {
        const uuid = generateCustomUUID()

        let searchPriceToUse = ""
        if (timestampParam) {
          const currentTimestamp = Date.now()
          const timestampDiffInSeconds =
            (currentTimestamp - Number(timestampParam)) / 1000

          if (timestampDiffInSeconds < 15 * 60) {
            searchPriceToUse = searchPriceParam
          }
        }
        const allowSameDay = user?.isAdmin
          ? true
          : bookingStore?.startDate
          ? isSameDay(parseISO(bookingStore.startDate), new Date())
          : false
        dispatch(
          loadPrequote({
            propertyId: property.property_id,
            preQuoteFlag: false,
            isRequestFromHost: dataToken === "E76xOs7B2s0",
            pmcId: property?.pmc_id,
            pmsSystem: property?.pms_system,
            isgoogle,
            prequoteRequestId: uuid,
            searchPrice: searchPriceToUse,
            allInPriceToggle: searchParams?.get("allInPriceToggle") === "true",
            directIncentive: isGvr,
            quoteId,
            allowSameDay,
            commissionRatePercent: bookingStore.commissionRatePercent ?? null,
          }),
        )
          .unwrap()
          .then((res) => {
            if (res.status && res.status == 200) {
              void (async (
                adultNumber,
                childNumber,
                pet,
                startDate,
                dayCount,
              ) => {
                const mixpanelV1Client = (await import("services/mixpanel"))
                  .mixpanelV1Client
                mixpanelV1Client.BOOKING_STORE_SUCCESS({
                  propertyId: property.property_id || "",
                  adultNumber: adultNumber || 0,
                  childNumber: childNumber || 0,
                  petsAllowed: pet || false,
                  startDate: startDate || "",
                  dayCount: dayCount,
                  prequoteID: res?.body?.prequoteResponseId || "",
                })
              })(
                bookingStore.adultNumber,
                bookingStore.childNumber,
                bookingStore.pet,
                bookingStore.startDate,
                bookingStore.dayCount,
              )
            }
            return
          })
          .catch((e) => {
            console.error(e)
          })
      }
      if (
        (bookingStore.adultNumber + bookingStore.childNumber >
          property.max_no_guests &&
          property.pms_system != PMS_SYSTEM_WHIMSTAY) ||
        (!property?.suitable_pet && bookingStore.pet)
      ) {
        dispatch(
          markBookingStore({
            startDate: null,
            endDate: null,
            adultNumber: 1,
            childNumber: 0,
            pet: false,
            error: "Those dates are not available.",
          }),
        )
      }
    }
    void loadData()
  }, [
    bookingStore.adultNumber,
    bookingStore.childNumber,
    bookingStore.petNumber,
    bookingStore.dayCount,
    bookingStore.endDate,
    bookingStore.pet,
    bookingStore.startDate,
    bookingStore.commissionRatePercent,
    isLoggedIn,
    isAgent,
    isTravelAgentClient,
    dispatch,
    property.max_no_guests,
    property.property_id,
    property.NumericPropertyId,
    property?.suitable_pet,
    property?.pmc_id,
    property?.pms_system,
    property.status,
    searchParams,
    user?.isAdmin,
  ])

  const onReserve = useCallback(() => {
    if (user?.userId && !isTrmsncndAcceptedByUser) {
      dispatch(
        markAuthStore({
          ui: {
            modalType: "SIGNUP_DETAILS_FORM",
          },
        }),
      )
      return
    }

    const isgoogle = searchParams?.get("isgoogle") === "true"
    const whimreferral = searchParams?.get("whimreferral")
    const isdirect = isgoogle || whimreferral ? true : false
    const quoteId =
      searchParams?.get("quoteId") || bookingStore?.commissionRatePercent
        ? bookingStore?.prequote?.prequoteResponseId
        : ""
    const allowSameDay = searchParams?.get("allowSameDay")
    const verification = searchParams?.get("verification") === "true"
    setLoaderDots(true)
    const params = new URLSearchParams(window.location.search)
    const backData = {
      timestemp1: params.get("timestamp"),
      startDate: params.get("check_in"),
      endDate: params.get("check_out"),
      adultsCount: params.get("adultsCount"),
      childrenCount: params.get("childrenCount"),
      petNumber: params.get("petNumber"),
    }
    const isVr = window.location.pathname.split("/")?.[3].includes("_vr")
    localStorage.setItem("backClick", JSON.stringify(backData))

    if (bookingStore.isDateAvailable) {
      const queryParams = new URLSearchParams({
        startDate: bookingStore.startDate || "",
        endDate: bookingStore.endDate || "",
        dayCount: `${bookingStore.dayCount}`,
        adultNumber: `${bookingStore.adultNumber}`,
        childNumber: `${bookingStore.childNumber}`,
        petNumber: `${bookingStore.petNumber}`,
        pet: bookingStore.pet ? "true" : "false",
        propertyId: bookingStore.propertyId || "",
        ...(isgoogle ? { isgoogle: "true" } : {}),
        ...(isdirect ? { isdirect: "true" } : {}),
        ...(whimreferral ? { whimreferral: whimreferral } : {}),
        ...(isVr ? { isVr: "true" } : {}),
        ...(quoteId ? { quoteId: quoteId } : {}),
        ...(allowSameDay ? { allowSameDay: allowSameDay } : {}),
        ...(verification ? { verification: "true" } : {}),
      })

      void router.push(`/payment?${queryParams.toString()}`)
    }
    if (!isLoggedIn) {
      localStorage.setItem(
        "mixpanelPageOrigin",
        "Property Details Page - Book Now",
      )
    }
  }, [
    searchParams,
    bookingStore,
    isLoggedIn,
    router,
    user?.userId,
    dispatch,
    isTrmsncndAcceptedByUser,
  ])

  const onBookClick = useCallback(() => {
    if (bookingStore.hasPrequote) {
      onReserve()
    } else {
      openDetailPageCalendar()
      dispatch(markBookingStore({ isCalendarOpen: true }))
    }
  }, [bookingStore.hasPrequote, onReserve, dispatch])

  const onCheckAvailabilityClick = useCallback(() => {
    if (bookingStore.hasPrequote) {
      onReserve()
    } else {
      openDetailPageCalendar()
      dispatch(markBookingStore({ isCalendarOpen: true }))
    }
  }, [bookingStore.hasPrequote, onReserve, dispatch])

  const translateBookBtn =
    bookingStore.buttonText === "Check Availability"
      ? t("btn.check")
      : "Book for Client"

  const isChooseYourDateCase =
    !bookingStore.isDateAvailable ||
    !bookingStore?.startDate ||
    !bookingStore?.endDate

  const openCalendar = useCallback(() => {
    openDetailPageCalendar()
    dispatch(markBookingStore({ isCalendarOpen: true }))
  }, [dispatch])

  // True while logged-in but user object hasn't hydrated yet — prevents flash of wrong state
  const isAuthPending = isLoggedIns && !user

  return {
    bookingStore,
    isLoggedIn,
    isAgent,
    isTravelAgentClient,
    isAuthPending,
    loaderDots,
    translateBookBtn,
    isChooseYourDateCase,
    onBookClick,
    onCheckAvailabilityClick,
    onReserve,
    openCalendar,
  }
}
